import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import SafetyShell from "../safety/SafetyShell";
import {
  Boxes,
  Download,
  FileUp,
  CheckCircle2,
  ClockIcon,
  History,
  Plus,
  Pencil,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { toISTInputValue, istInputToUTC } from "../lib/datetime";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// --- datetime helpers (Option A: precise cutover) ---
// `effective_date` is stored as a UTC ISO string with +00:00 offset, e.g.
// "2026-05-27T08:00:00+00:00". Legacy date-only values like "2026-05-27" are
// still valid (treated as 00:00 UTC).

/** Current local time formatted for <input type="datetime-local"> ("YYYY-MM-DDTHH:MM"). */
function nowForDatetimeLocal() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Convert local "YYYY-MM-DDTHH:MM" → UTC ISO "YYYY-MM-DDTHH:MM:SS+00:00". */
function localToUtcIso(localStr) {
  if (!localStr) return "";
  const d = new Date(localStr); // parses as local time
  if (Number.isNaN(d.getTime())) return localStr;
  const iso = d.toISOString(); // "2026-05-27T08:00:00.000Z"
  return iso.replace(/\.\d{3}Z$/, "+00:00");
}

/** Format a stored cutover datetime for friendly display (local TZ). */
function fmtDatetime(stored) {
  if (!stored) return "—";
  // Legacy date-only values like "2026-05-27" — display as date only.
  if (!stored.includes("T")) return stored;
  const d = new Date(stored);
  if (Number.isNaN(d.getTime())) return stored;
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const KIND_LABEL = {
  ERP_OPENING: "ERP Opening Balance",
  CUTOVER: "Cutover Opening Balance",
};

const STATUS_TONE = {
  ACTIVE: "bg-emerald-100 text-emerald-800 border-emerald-200",
  DRAFT: "bg-amber-100 text-amber-800 border-amber-200",
  SUPERSEDED: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

function KindBlock({ kind, active, revisions, onCreate, onActivate, onDelete, onView, onViewHistory, onEditEffective, onViewEffHistory, canManage }) {
  return (
    <div className="sf-surface p-4 mb-4" data-testid={`kind-block-${kind}`}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Boxes className={`w-5 h-5 ${kind === "CUTOVER" ? "text-emerald-600" : "text-sky-600"}`} />
          <div>
            <h2 className="text-base font-semibold">{KIND_LABEL[kind]}</h2>
            <p className="text-xs text-zinc-500">
              {kind === "CUTOVER"
                ? "Physical count snapshot. Starts the trusted ledger — scan production and packing-list dispatch after this date count toward Live Inventory."
                : "Earliest seed for the legacy ledger — anchors the closing balance calculation for pre-cutover (unreliable) DPRs."}
            </p>
          </div>
        </div>
        {canManage && (
          <Button size="sm" onClick={() => onCreate(kind)} data-testid={`create-${kind}-btn`}>
            <Plus className="w-4 h-4 mr-1.5" />
            New revision
          </Button>
        )}
      </div>

      {/* Active highlight */}
      <div className="mb-3 rounded border bg-zinc-50 p-3 flex items-center gap-3 flex-wrap text-sm" data-testid={`active-${kind}`}>
        {active ? (
          <>
            <Badge className={STATUS_TONE.ACTIVE}>
              <CheckCircle2 className="w-3 h-3 mr-1" /> Active v{active.revision_no}
            </Badge>
            <span className="text-zinc-600">
              Effective <span className="font-medium">{fmtDatetime(active.effective_date)}</span>
            </span>
            <span className="text-zinc-400">·</span>
            <span className="text-zinc-600">{active.line_count ?? "—"} line items</span>
            <span className="text-zinc-400">·</span>
            <span className="text-zinc-600">{active.total_pieces?.toLocaleString() ?? "—"} pieces</span>
            <button
              onClick={() => onView(active.id)}
              className="ml-auto text-emerald-700 hover:underline text-xs"
              data-testid={`view-active-${kind}`}
            >
              View lines →
            </button>
          </>
        ) : (
          <span className="text-zinc-500 italic">
            <AlertTriangle className="w-3.5 h-3.5 inline mr-1 text-amber-500" />
            No active revision yet
          </span>
        )}
      </div>

      {/* History */}
      <div>
        <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
          <History className="w-3.5 h-3.5" /> Revision history ({revisions.length})
        </div>
        {revisions.length === 0 ? (
          <div className="text-xs text-zinc-400 italic">No revisions yet</div>
        ) : (
          <div className="overflow-x-auto rounded border bg-white">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 text-zinc-600">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">Rev</th>
                  <th className="px-2 py-1.5 text-left font-medium">Status</th>
                  <th className="px-2 py-1.5 text-left font-medium">Effective</th>
                  <th className="px-2 py-1.5 text-right font-medium">Lines</th>
                  <th className="px-2 py-1.5 text-right font-medium">Pieces</th>
                  <th className="px-2 py-1.5 text-left font-medium">Reason</th>
                  <th className="px-2 py-1.5 text-left font-medium">Created</th>
                  <th className="px-2 py-1.5 text-right font-medium w-44">Actions</th>
                </tr>
              </thead>
              <tbody>
                {revisions.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-zinc-50" data-testid={`rev-row-${r.id}`}>
                    <td className="px-2 py-1.5 font-mono">v{r.revision_no}</td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Badge className={STATUS_TONE[r.status]} variant="outline">
                          {r.status}
                        </Badge>
                        {r.edited_lines > 0 && (
                          <button
                            onClick={() => onViewHistory(r)}
                            className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 hover:bg-amber-100 transition-colors"
                            title="View opening-balance edit history"
                            data-testid={`edited-badge-${r.id}`}
                          >
                            <History className="w-3 h-3" /> edited ({r.edited_lines})
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1">
                        <span>{fmtDatetime(r.effective_date)}</span>
                        {canManage && r.status !== "SUPERSEDED" && (
                          <button
                            onClick={() => onEditEffective(r)}
                            className="text-zinc-400 hover:text-orange-600 transition-colors"
                            title="Edit effective date & time"
                            data-testid={`edit-eff-${r.id}`}
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        )}
                        {r.eff_edits > 0 && (
                          <button
                            onClick={() => onViewEffHistory(r)}
                            className="inline-flex items-center gap-0.5 rounded border border-sky-200 bg-sky-50 px-1 py-0.5 text-[10px] font-medium text-sky-700 hover:bg-sky-100 transition-colors"
                            title="View effective-date change history"
                            data-testid={`eff-history-badge-${r.id}`}
                          >
                            <History className="w-3 h-3" /> {r.eff_edits}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-right">{r.line_count}</td>
                    <td className="px-2 py-1.5 text-right">{r.total_pieces?.toLocaleString()}</td>
                    <td className="px-2 py-1.5 truncate max-w-[260px]" title={r.reason}>{r.reason || "—"}</td>
                    <td className="px-2 py-1.5 text-zinc-500">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}<br />
                      <span className="text-[10px]">{r.created_by}</span>
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => onView(r.id)} data-testid={`view-${r.id}`}>
                          View
                        </Button>
                        {canManage && r.status === "DRAFT" && (
                          <>
                            <Button
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => onActivate(r)}
                              data-testid={`activate-${r.id}`}
                            >
                              Activate
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                              onClick={() => onDelete(r)}
                              title="Delete draft"
                              data-testid={`delete-${r.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function InventoryCutoverSetup({ user, onLogout }) {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState({ erp_opening: null, cutover: null });
  const [revs, setRevs] = useState({ ERP_OPENING: [], CUTOVER: [] });

  const [uploadDialog, setUploadDialog] = useState({ open: false, kind: null });
  const [form, setForm] = useState({ effective_date: "", reason: "" });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const [detailDialog, setDetailDialog] = useState({ open: false, id: null, doc: null, loading: false });
  const [historyDialog, setHistoryDialog] = useState({ open: false, rev: null, entries: null, loading: false });
  const [activateDialog, setActivateDialog] = useState({ open: false, rev: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, rev: null });
  const [effDialog, setEffDialog] = useState({ open: false, rev: null });
  const [effVal, setEffVal] = useState("");
  const [effReason, setEffReason] = useState("");
  const [effHistDialog, setEffHistDialog] = useState({ open: false, rev: null, entries: null, loading: false });
  const [busy, setBusy] = useState(false);

  const canManage = permissions.includes("inventory.manage");

  const load = async () => {
    setLoading(true);
    try {
      const [perms, act, all] = await Promise.all([
        axios.get(`${API}/auth/me/permissions`, auth()).catch(() => ({ data: { effective_permissions: [] } })),
        axios.get(`${API}/inventory/baselines/active`, auth()),
        axios.get(`${API}/inventory/baselines`, auth()),
      ]);
      setPermissions(perms.data.effective_permissions || []);
      setActive(act.data || { erp_opening: null, cutover: null });
      const grouped = { ERP_OPENING: [], CUTOVER: [] };
      (all.data.items || []).forEach((r) => {
        if (grouped[r.kind]) grouped[r.kind].push(r);
      });
      setRevs(grouped);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/inventory/baselines/template.csv`, {
        ...auth(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "inventory_baseline_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Template download failed");
    }
  };

  const openCreate = (kind) => {
    setForm({ effective_date: nowForDatetimeLocal(), reason: "" });
    if (fileRef.current) fileRef.current.value = "";
    setUploadDialog({ open: true, kind });
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!form.effective_date) {
      toast.error("Please set an effective date first");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    const ok = [".csv", ".xlsx", ".xlsm"].some((ext) => file.name.toLowerCase().endsWith(ext));
    if (!ok) {
      toast.error("Only .csv / .xlsx files are supported");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", uploadDialog.kind);
      fd.append("effective_date", localToUtcIso(form.effective_date));
      fd.append("reason", form.reason || "");
      const r = await axios.post(`${API}/inventory/baselines/upload`, fd, {
        headers: { ...auth().headers, "Content-Type": "multipart/form-data" },
      });
      const data = r.data;
      const warns = data.warnings || [];
      toast.success(
        `Draft v${data.revision_no} created — ${data.line_count} lines parsed${warns.length ? ` · ${warns.length} warning${warns.length === 1 ? "" : "s"}` : ""}`,
        { duration: 6000 },
      );
      setUploadDialog({ open: false, kind: null });
      await load();
    } catch (err) {
      const det = err.response?.data?.detail;
      if (det && typeof det === "object" && det.warnings) {
        toast.error(`${det.error}: ${det.warnings.join("; ")}`, { duration: 8000 });
      } else {
        toast.error(det || "Upload failed");
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const viewHistory = async (rev) => {
    setHistoryDialog({ open: true, rev, entries: null, loading: true });
    try {
      const r = await axios.get(`${API}/inventory/baselines/${rev.id}/edit-history`, auth());
      setHistoryDialog({ open: true, rev, entries: r.data.entries || [], loading: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load edit history");
      setHistoryDialog({ open: false, rev: null, entries: null, loading: false });
    }
  };

  const viewDetail = async (id) => {
    setDetailDialog({ open: true, id, doc: null, loading: true });
    try {
      const r = await axios.get(`${API}/inventory/baselines/${id}`, auth());
      setDetailDialog({ open: true, id, doc: r.data, loading: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load detail");
      setDetailDialog({ open: false, id: null, doc: null, loading: false });
    }
  };

  // Inline edit of an opening-balance line (audit-logged on the backend)
  const [editLn, setEditLn] = useState(null);
  const [editP, setEditP] = useState("");
  const [editK, setEditK] = useState("");
  const [editReason, setEditReason] = useState("");
  const [savingLn, setSavingLn] = useState(false);
  const [addingLine, setAddingLine] = useState(false);
  const [newLine, setNewLine] = useState({ product_code: "", po_number: "", customer_name: "", qty_pieces: "", qty_kg: "", reason: "" });
  const [savingNew, setSavingNew] = useState(false);
  const addLine = async () => {
    if (!newLine.product_code.trim()) { toast.error("Product code required"); return; }
    setSavingNew(true);
    try {
      const body = {
        product_code: newLine.product_code.trim(),
        po_number: newLine.po_number.trim() || null,
        customer_name: newLine.customer_name.trim(),
        qty_pieces: Number(newLine.qty_pieces || 0),
        qty_kg: newLine.qty_kg === "" ? null : Number(newLine.qty_kg),
        reason: newLine.reason.trim(),
      };
      await axios.post(`${API}/inventory/baselines/${detailDialog.id}/lines`, body, auth());
      toast.success("Product added to opening balance & logged");
      setAddingLine(false);
      setNewLine({ product_code: "", po_number: "", customer_name: "", qty_pieces: "", qty_kg: "", reason: "" });
      await viewDetail(detailDialog.id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Add failed");
    } finally {
      setSavingNew(false);
    }
  };
  const startEditLine = (ln) => {
    setEditLn(ln.line_no);
    setEditP(String(ln.qty_pieces ?? ""));
    setEditK(ln.qty_kg == null ? "" : String(ln.qty_kg));
    setEditReason("");
  };
  const saveLine = async (ln) => {
    const body = { reason: editReason };
    if (editP !== "" && Number(editP) !== (ln.qty_pieces ?? 0)) body.qty_pieces = Number(editP);
    const kgNow = ln.qty_kg == null ? "" : String(ln.qty_kg);
    if (editK !== kgNow) body.qty_kg = editK === "" ? null : Number(editK);
    if (body.qty_pieces === undefined && !("qty_kg" in body)) { setEditLn(null); return; }
    setSavingLn(true);
    try {
      await axios.patch(`${API}/inventory/baselines/${detailDialog.id}/lines/${ln.line_no}`, body, auth());
      toast.success("Opening balance updated & logged");
      setEditLn(null);
      await viewDetail(detailDialog.id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSavingLn(false);
    }
  };


  const openEffEdit = (rev) => {
    setEffVal(toISTInputValue(rev.effective_date) || "");
    setEffReason("");
    setEffDialog({ open: true, rev });
  };

  const viewEffHistory = async (rev) => {
    setEffHistDialog({ open: true, rev, entries: null, loading: true });
    try {
      const r = await axios.get(`${API}/inventory/baselines/${rev.id}/effective-date-history`, auth());
      setEffHistDialog({ open: true, rev, entries: r.data.entries || [], loading: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load history");
      setEffHistDialog({ open: false, rev: null, entries: null, loading: false });
    }
  };

  const saveEffDate = async () => {
    if (!effDialog.rev) return;
    if (!effVal) { toast.error("Pick a date & time"); return; }
    setBusy(true);
    try {
      await axios.patch(`${API}/inventory/baselines/${effDialog.rev.id}/effective-date`,
        { effective_date: istInputToUTC(effVal), reason: effReason || "" }, auth());
      toast.success("Effective date updated & logged");
      setEffDialog({ open: false, rev: null });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const confirmActivate = async () => {
    if (!activateDialog.rev) return;
    setBusy(true);
    try {      await axios.post(`${API}/inventory/baselines/${activateDialog.rev.id}/activate`, {}, auth());
      toast.success(`Activated ${KIND_LABEL[activateDialog.rev.kind]} v${activateDialog.rev.revision_no}`);
      setActivateDialog({ open: false, rev: null });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Activation failed");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteDialog.rev) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/inventory/baselines/${deleteDialog.rev.id}`, auth());
      toast.success("Draft deleted");
      setDeleteDialog({ open: false, rev: null });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[{ label: "Safety", to: "/safety" }, { label: "Inventory", to: "/safety/inventory" }, { label: "Cutover Setup" }]}
      miniMap={[
        { label: "Safety", to: "/safety", depth: 0 },
        { label: "Inventory", to: "/safety/inventory", depth: 1 },
        { label: "Cutover Setup", active: true, depth: 2 },
      ]}
    >
      <Toaster richColors position="top-right" />
      <div data-testid="inventory-cutover-setup-page">
        {/* Header / template download */}
        <div className="sf-surface p-4 mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Boxes className="w-5 h-5 text-emerald-600" />
            <div>
              <h1 className="text-xl font-bold tracking-tight">Inventory Baseline Setup</h1>
              <p className="text-xs text-zinc-500">
                Set the ERP Opening Balance (legacy anchor) and the Cutover Opening Balance (physical count). Each can have multiple
                versioned revisions — only one is Active at a time.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="dl-template-btn">
            <Download className="w-4 h-4 mr-1.5" />
            Download CSV template
          </Button>
        </div>

        {loading ? (
          <div className="sf-surface p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto" />
          </div>
        ) : (
          <>
            <KindBlock
              kind="CUTOVER"
              active={active.cutover}
              revisions={revs.CUTOVER}
              onCreate={openCreate}
              onActivate={(r) => setActivateDialog({ open: true, rev: r })}
              onDelete={(r) => setDeleteDialog({ open: true, rev: r })}
              onView={viewDetail}
              onViewHistory={viewHistory}
              onEditEffective={openEffEdit}
              onViewEffHistory={viewEffHistory}
              canManage={canManage}
            />
            <KindBlock
              kind="ERP_OPENING"
              active={active.erp_opening}
              revisions={revs.ERP_OPENING}
              onCreate={openCreate}
              onActivate={(r) => setActivateDialog({ open: true, rev: r })}
              onDelete={(r) => setDeleteDialog({ open: true, rev: r })}
              onView={viewDetail}
              onViewHistory={viewHistory}
              onEditEffective={openEffEdit}
              onViewEffHistory={viewEffHistory}
              canManage={canManage}
            />
          </>
        )}
      </div>

      {/* Upload dialog */}
      <Dialog open={uploadDialog.open} onOpenChange={(o) => !o && !uploading && setUploadDialog({ open: false, kind: null })}>
        <DialogContent data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>New {uploadDialog.kind ? KIND_LABEL[uploadDialog.kind] : ""} revision</DialogTitle>
            <DialogDescription>
              Set an effective date, optional reason, then upload your physical-count file (CSV or XLSX). The revision is created
              as DRAFT — activate it when you're ready.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-zinc-500">Effective date &amp; time *</Label>
              <Input
                type="datetime-local"
                value={form.effective_date}
                onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                data-testid="form-effective-date"
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                Set this to the exact moment of your physical stock count. Scans before this time are absorbed into the
                Opening Balance; scans after are counted as new production. Your local timezone — stored internally as UTC.
              </p>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Reason / notes</Label>
              <Input
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="e.g. Initial physical count May 2026"
                data-testid="form-reason"
              />
            </div>
            <div className="text-xs text-zinc-500 leading-relaxed">
              CSV columns: <span className="font-mono">product_code, description, po_number, customer_name, qty_pieces, qty_kg, notes</span>.
              Only <span className="font-mono">product_code</span> + <span className="font-mono">qty_pieces</span> are required.
              <button onClick={downloadTemplate} className="text-emerald-700 hover:underline ml-1">
                Download template
              </button>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xlsm"
              className="hidden"
              onChange={handleUploadFile}
              data-testid="form-file-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog({ open: false, kind: null })} disabled={uploading}>
              Cancel
            </Button>
            <Button onClick={() => fileRef.current?.click()} disabled={uploading || !form.effective_date} data-testid="form-upload-btn">
              {uploading ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Uploading…
                </>
              ) : (
                <>
                  <FileUp className="w-4 h-4 mr-1.5" />
                  Choose file & upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(o) => !o && setDetailDialog({ open: false, id: null, doc: null, loading: false })}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="detail-dialog">
          <DialogHeader>
            <DialogTitle>
              {detailDialog.doc ? `${KIND_LABEL[detailDialog.doc.kind]} v${detailDialog.doc.revision_no}` : "Loading…"}
            </DialogTitle>
            <DialogDescription>
              {detailDialog.doc && (
                <span>
                  <Badge className={STATUS_TONE[detailDialog.doc.status]}>{detailDialog.doc.status}</Badge>
                  <span className="ml-2">Effective {fmtDatetime(detailDialog.doc.effective_date)}</span>
                  <span className="ml-2 text-zinc-500">· created by {detailDialog.doc.created_by}</span>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {detailDialog.loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto" />
            </div>
          ) : detailDialog.doc ? (
            <div>
              {detailDialog.doc.reason && (
                <div className="text-xs text-zinc-600 mb-3 p-2 bg-zinc-50 rounded border">
                  <span className="font-medium">Reason: </span>{detailDialog.doc.reason}
                </div>
              )}
              {(detailDialog.doc.upload_warnings || []).length > 0 && (
                <div className="text-xs text-amber-700 mb-3 p-2 bg-amber-50 rounded border border-amber-200">
                  <div className="font-medium mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Upload warnings
                  </div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {detailDialog.doc.upload_warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              {canManage && (
                <div className="flex justify-end mb-2">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setAddingLine((v) => !v)} data-testid="baseline-add-line-toggle">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add product
                  </Button>
                </div>
              )}
              <div className="overflow-x-auto rounded border bg-white">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50 text-zinc-600">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium">#</th>
                      <th className="px-2 py-1.5 text-left font-medium">Product</th>
                      <th className="px-2 py-1.5 text-left font-medium">PO</th>
                      <th className="px-2 py-1.5 text-left font-medium">Customer</th>
                      <th className="px-2 py-1.5 text-right font-medium">Pieces</th>
                      <th className="px-2 py-1.5 text-right font-medium">Kg</th>
                      {canManage && <th className="px-2 py-1.5 text-right font-medium">Edit</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {canManage && addingLine && (
                      <tr className="border-t bg-emerald-50/60" data-testid="baseline-add-line-row">
                        <td className="px-2 py-1 text-zinc-400">+</td>
                        <td className="px-2 py-1"><Input value={newLine.product_code} onChange={(e) => setNewLine({ ...newLine, product_code: e.target.value })} placeholder="Product code" className="h-7 w-32 text-xs" data-testid="baseline-new-product" /></td>
                        <td className="px-2 py-1"><Input value={newLine.po_number} onChange={(e) => setNewLine({ ...newLine, po_number: e.target.value })} placeholder="PO (opt)" className="h-7 w-24 text-xs" data-testid="baseline-new-po" /></td>
                        <td className="px-2 py-1"><Input value={newLine.customer_name} onChange={(e) => setNewLine({ ...newLine, customer_name: e.target.value })} placeholder="Customer (opt)" className="h-7 w-28 text-xs" data-testid="baseline-new-customer" /></td>
                        <td className="px-2 py-1 text-right"><Input type="number" value={newLine.qty_pieces} onChange={(e) => setNewLine({ ...newLine, qty_pieces: e.target.value })} placeholder="Pieces" className="h-7 w-20 text-right text-xs ml-auto" data-testid="baseline-new-pieces" /></td>
                        <td className="px-2 py-1 text-right"><Input type="number" value={newLine.qty_kg} onChange={(e) => setNewLine({ ...newLine, qty_kg: e.target.value })} placeholder="Kg" className="h-7 w-20 text-right text-xs ml-auto" data-testid="baseline-new-kg" /></td>
                        <td className="px-2 py-1 text-right whitespace-nowrap">
                          <div className="flex items-center gap-1 justify-end">
                            <Input placeholder="reason" value={newLine.reason} onChange={(e) => setNewLine({ ...newLine, reason: e.target.value })} className="h-7 w-24 text-xs" data-testid="baseline-new-reason" />
                            <Button size="sm" className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700" disabled={savingNew} onClick={addLine} data-testid="baseline-new-save">Add</Button>
                            <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setAddingLine(false)} data-testid="baseline-new-cancel">✕</Button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {(detailDialog.doc.lines || []).map((ln) => {
                      const editing = editLn === ln.line_no;
                      return (
                      <tr key={ln.line_no} className={`border-t ${editing ? "bg-blue-50/60" : ""} ${ln.edited_by ? "bg-amber-50/40" : ""}`} data-testid={`baseline-line-${ln.line_no}`}>
                        <td className="px-2 py-1 text-zinc-500">{ln.line_no}</td>
                        <td className="px-2 py-1">
                          <div className="font-mono">{ln.product_code}</div>
                          {ln.description && <div className="text-[10px] text-zinc-500 truncate max-w-[220px]">{ln.description}</div>}
                        </td>
                        <td className="px-2 py-1 font-mono">{ln.po_number || <span className="italic text-zinc-400">Unassigned</span>}</td>
                        <td className="px-2 py-1 truncate max-w-[160px]">{ln.customer_name || "—"}</td>
                        <td className="px-2 py-1 text-right">
                          {editing
                            ? <Input type="number" value={editP} onChange={(e) => setEditP(e.target.value)} className="h-7 w-24 text-right ml-auto" data-testid={`baseline-edit-pieces-${ln.line_no}`} />
                            : <span title={ln.edited_by ? `Edited by ${ln.edited_by}` : ""}>{ln.qty_pieces?.toLocaleString()}{ln.edited_by && " *"}</span>}
                        </td>
                        <td className="px-2 py-1 text-right text-zinc-500">
                          {editing
                            ? <Input type="number" value={editK} onChange={(e) => setEditK(e.target.value)} className="h-7 w-24 text-right ml-auto" data-testid={`baseline-edit-kg-${ln.line_no}`} />
                            : (ln.qty_kg ?? "—")}
                        </td>
                        {canManage && (
                          <td className="px-2 py-1 text-right whitespace-nowrap">
                            {editing ? (
                              <div className="flex items-center gap-1 justify-end">
                                <Input placeholder="reason" value={editReason} onChange={(e) => setEditReason(e.target.value)} className="h-7 w-28 text-xs" data-testid={`baseline-edit-reason-${ln.line_no}`} />
                                <Button size="sm" className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700" disabled={savingLn} onClick={() => saveLine(ln)} data-testid={`baseline-save-line-${ln.line_no}`}>Save</Button>
                                <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setEditLn(null)} data-testid={`baseline-cancel-line-${ln.line_no}`}>✕</Button>
                              </div>
                            ) : (
                              <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => startEditLine(ln)} data-testid={`baseline-edit-line-${ln.line_no}`}>✎</Button>
                            )}
                          </td>
                        )}
                      </tr>
                      );
                    })}
                    {(detailDialog.doc.lines || []).length === 0 && (
                      <tr><td colSpan={canManage ? 7 : 6} className="p-4 text-center text-zinc-400 italic">No lines</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Edit history dialog */}
      <Dialog open={historyDialog.open} onOpenChange={(o) => !o && setHistoryDialog({ open: false, rev: null, entries: null, loading: false })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-amber-600" />
              Opening-balance edit history
            </DialogTitle>
            <DialogDescription>
              {historyDialog.rev && (
                <span>
                  {KIND_LABEL[historyDialog.rev.kind]} <strong>v{historyDialog.rev.revision_no}</strong> — every manual adjustment made to opening-balance lines, most recent first.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {historyDialog.loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600 mx-auto" />
            </div>
          ) : (historyDialog.entries || []).length === 0 ? (
            <div className="p-6 text-center text-sm text-zinc-400 italic">No edits recorded yet.</div>
          ) : (
            <div className="space-y-2" data-testid="history-entries">
              {historyDialog.entries.map((e, i) => (
                <div key={i} className="rounded border bg-white p-3 text-sm" data-testid={`history-entry-${i}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
                    <span className="font-mono font-medium text-zinc-800">{e.product_code || "—"}</span>
                    <span className="text-[11px] text-zinc-500">{fmtDatetime(e.at)}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-1.5">
                    {(e.changes || []).map((c, ci) => (
                      <span key={ci} className="inline-block rounded bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[11px] font-mono text-amber-800">
                        {c}
                      </span>
                    ))}
                  </div>
                  {e.reason && (
                    <div className="text-xs text-zinc-600"><span className="font-medium">Reason:</span> {e.reason}</div>
                  )}
                  <div className="text-[11px] text-zinc-400 mt-1">by {e.by || "—"}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Effective-date change history */}
      <Dialog open={effHistDialog.open} onOpenChange={(o) => !o && setEffHistDialog({ open: false, rev: null, entries: null, loading: false })}>
        <DialogContent className="max-w-lg" data-testid="eff-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-sky-600" /> Effective date/time history
            </DialogTitle>
            <DialogDescription>
              {effHistDialog.rev && (
                <span>{KIND_LABEL[effHistDialog.rev.kind]} <strong>v{effHistDialog.rev.revision_no}</strong> — every change to the cutover boundary, most recent first.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          {effHistDialog.loading ? (
            <div className="p-6 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sky-600 mx-auto" /></div>
          ) : (effHistDialog.entries || []).length === 0 ? (
            <div className="p-6 text-center text-sm text-zinc-400 italic">No changes recorded.</div>
          ) : (
            <div className="space-y-2" data-testid="eff-history-entries">
              {effHistDialog.entries.map((e, i) => (
                <div key={i} className="rounded border bg-white p-3 text-sm" data-testid={`eff-history-entry-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-mono text-xs text-zinc-500 line-through">{fmtDatetime(e.old)}</span>
                    <span className="text-zinc-400">→</span>
                    <span className="font-mono text-xs font-semibold text-sky-700">{fmtDatetime(e.new)}</span>
                  </div>
                  {e.reason && <div className="text-xs text-zinc-600"><span className="font-medium">Reason:</span> {e.reason}</div>}
                  <div className="text-[11px] text-zinc-400 mt-1">by {e.by || "—"} · {fmtDatetime(e.at)}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit effective date/time */}
      <Dialog open={effDialog.open} onOpenChange={(o) => !o && !busy && setEffDialog({ open: false, rev: null })}>
        <DialogContent data-testid="eff-dialog">
          <DialogHeader>
            <DialogTitle>Edit effective date &amp; time</DialogTitle>
            <DialogDescription>
              {effDialog.rev && (
                <span>{KIND_LABEL[effDialog.rev.kind]} <strong>v{effDialog.rev.revision_no}</strong>. This is the cutover boundary — pallets produced after it count as new production. Changing it on an active baseline immediately affects live inventory.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-1 space-y-2">
            <Label className="text-xs text-zinc-500">Effective date &amp; time (IST)</Label>
            <Input
              type="datetime-local"
              value={effVal}
              onChange={(e) => setEffVal(e.target.value)}
              data-testid="eff-datetime-input"
            />
            <Label className="text-xs text-zinc-500 pt-1">Reason (optional)</Label>
            <Input
              value={effReason}
              onChange={(e) => setEffReason(e.target.value)}
              placeholder="e.g. Correcting to the actual count freeze time"
              data-testid="eff-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEffDialog({ open: false, rev: null })} disabled={busy}>Cancel</Button>
            <Button onClick={saveEffDate} disabled={busy} data-testid="eff-save-btn">
              {busy ? "Saving…" : "Save effective date"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activate confirm */}
      <Dialog open={activateDialog.open} onOpenChange={(o) => !o && !busy && setActivateDialog({ open: false, rev: null })}>
        <DialogContent data-testid="activate-dialog">
          <DialogHeader>
            <DialogTitle>Activate revision?</DialogTitle>
            <DialogDescription>
              {activateDialog.rev && (
                <span>
                  Activating <strong>{KIND_LABEL[activateDialog.rev.kind]} v{activateDialog.rev.revision_no}</strong> will supersede the current active revision (if any) of the same kind.<br /><br />
                  Effective date &amp; time: <span className="font-mono">{fmtDatetime(activateDialog.rev.effective_date)}</span><br />
                  Line items: {activateDialog.rev.line_count}<br />
                  Total pieces: {activateDialog.rev.total_pieces?.toLocaleString()}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivateDialog({ open: false, rev: null })} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={confirmActivate} disabled={busy} data-testid="confirm-activate-btn">
              {busy ? "Activating…" : "Activate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteDialog.open} onOpenChange={(o) => !o && !busy && setDeleteDialog({ open: false, rev: null })}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Delete draft?
            </DialogTitle>
            <DialogDescription>
              {deleteDialog.rev && (
                <span>
                  Delete {KIND_LABEL[deleteDialog.rev.kind]} <strong>v{deleteDialog.rev.revision_no}</strong> (DRAFT)?
                  This permanently removes the revision and its {deleteDialog.rev.line_count} line items.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, rev: null })} disabled={busy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={busy} data-testid="confirm-delete-btn">
              {busy ? "Deleting…" : "Delete draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SafetyShell>
  );
}

export default InventoryCutoverSetup;
